import { useEffect, useState } from 'react'
import {
  Box,
  Card,
  CardBody,
  Skeleton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Text,
  Alert,
  AlertIcon,
} from '@chakra-ui/react'
import ReportPageHeader from '@/components/reports/ReportPageHeader'
import {
  helpdeskReportService,
  type ReportFilters,
  type TicketProductivityGrainRow,
  type TicketProductivityHandlingRow,
  type TicketProductivityRow,
} from '@/lib/services/helpdeskReportService'

type View = 'handling' | 'daily' | 'monthly'

const VIEW_INDEX: View[] = ['handling', 'daily', 'monthly']

export default function TicketProductivityPage() {
  const [filters, setFilters] = useState<ReportFilters>({})
  const [view, setView] = useState<View>('handling')
  const [rows, setRows] = useState<TicketProductivityRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    helpdeskReportService
      .getTicketProductivity(filters, view)
      .then((res) => setRows(res.rows ?? []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false))
  }, [filters.from, filters.to, filters.client_id, filters.team_id, view])

  return (
    <Box p={4}>
      <ReportPageHeader
        title="Ticket Productivity"
        subtitle="Ticket handling plus daily and monthly productivity — derived from RAW FD (closed/created tickets)"
        reportType="ticket-productivity"
        filters={{ ...filters, view }}
        onFiltersChange={(f) => {
          setFilters({
            client_id: f.client_id,
            from: f.from,
            to: f.to,
            team_id: f.team_id,
          })
        }}
      />

      <Alert status="info" mb={4} borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">
          One report with three views (lean). Excel download includes all three sheets. For raw extracts and FE
          job summary, use SD Agent Productivity.
        </Text>
      </Alert>

      <Card>
        <CardBody>
          <Tabs
            index={VIEW_INDEX.indexOf(view)}
            onChange={(i) => setView(VIEW_INDEX[i] ?? 'handling')}
            variant="enclosed"
            size="sm"
            colorScheme="blue"
          >
            <TabList>
              <Tab fontWeight="600">Ticket Handling</Tab>
              <Tab fontWeight="600">Daily</Tab>
              <Tab fontWeight="600">Monthly</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0}>
                {loading ? (
                  <Skeleton height="220px" />
                ) : (
                  <Box overflowX="auto">
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>Agent</Th>
                          <Th isNumeric>Closed</Th>
                          <Th isNumeric>Incident</Th>
                          <Th isNumeric>Service Req</Th>
                          <Th isNumeric>Avg Children</Th>
                          <Th isNumeric>% Volume</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {(rows as TicketProductivityHandlingRow[]).length === 0 ? (
                          <Tr>
                            <Td colSpan={6}>
                              <Text fontSize="sm" color="gray.500">
                                No closed tickets in this period.
                              </Text>
                            </Td>
                          </Tr>
                        ) : (
                          (rows as TicketProductivityHandlingRow[]).map((r) => (
                            <Tr key={r.agent}>
                              <Td>{r.agent}</Td>
                              <Td isNumeric>{r.tickets_closed}</Td>
                              <Td isNumeric>{r.incident_requests}</Td>
                              <Td isNumeric>{r.service_requests}</Td>
                              <Td isNumeric>{r.avg_child_tickets}</Td>
                              <Td isNumeric>{(Number(r.pct_of_total_volume) * 100).toFixed(1)}%</Td>
                            </Tr>
                          ))
                        )}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </TabPanel>
              <TabPanel px={0}>
                <GrainTable loading={loading} rows={rows as TicketProductivityGrainRow[]} />
              </TabPanel>
              <TabPanel px={0}>
                <GrainTable loading={loading} rows={rows as TicketProductivityGrainRow[]} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </CardBody>
      </Card>
    </Box>
  )
}

function GrainTable({ loading, rows }: { loading: boolean; rows: TicketProductivityGrainRow[] }) {
  if (loading) return <Skeleton height="220px" />

  return (
    <Box overflowX="auto">
      <Table size="sm">
        <Thead>
          <Tr>
            <Th>Period</Th>
            <Th>Agent</Th>
            <Th isNumeric>Created</Th>
            <Th isNumeric>Closed</Th>
            <Th isNumeric>Incident</Th>
            <Th isNumeric>Service Req</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.length === 0 ? (
            <Tr>
              <Td colSpan={6}>
                <Text fontSize="sm" color="gray.500">
                  No ticket activity in this period.
                </Text>
              </Td>
            </Tr>
          ) : (
            rows.map((r) => (
              <Tr key={`${r.period}-${r.agent}`}>
                <Td whiteSpace="nowrap">{r.period}</Td>
                <Td>{r.agent}</Td>
                <Td isNumeric>{r.tickets_created}</Td>
                <Td isNumeric>{r.tickets_closed}</Td>
                <Td isNumeric>{r.incident_requests}</Td>
                <Td isNumeric>{r.service_requests}</Td>
              </Tr>
            ))
          )}
        </Tbody>
      </Table>
    </Box>
  )
}
